import { useCallback } from 'react';
import { useExecuteWorkflowNodeMutation } from '../useExecuteWorkflowNodeMutation';
import useMutationHelpers from '../useMutationHelpers';
import type {
  GetUserPreferencePayload,
  SetUserPreferencePayload,
  UserPreferenceEntity,
} from './utils';
import {
  buildGetUserPreferencePayloadRequest,
  buildSetUserPreferencePayloadRequest,
} from './utils';

type Args = { mutationOptions?: Parameters<typeof useExecuteWorkflowNodeMutation>[0] };

export default function useUserPreferenceMutation({ mutationOptions }: Args = {}) {
  const {
    isPending: isGetPending,
    mutateAsync: getMutateAsync,
    error: getError,
  } = useExecuteWorkflowNodeMutation(mutationOptions);
  const {
    isPending: isSetPending,
    mutateAsync: setMutateAsync,
    error: setError,
  } = useExecuteWorkflowNodeMutation(mutationOptions);
  const { onShowErrorSnackbar, onMutationError } = useMutationHelpers();

  const getUserPreference = useCallback(
    async (params: GetUserPreferencePayload) => {
      try {
        const result = await getMutateAsync(
          { data: buildGetUserPreferencePayloadRequest(params) },
          { onError: onMutationError },
        );

        return (result.response as { userPreferences: UserPreferenceEntity[] } | undefined)
          ?.userPreferences;
      } catch (error) {
        onShowErrorSnackbar();
      }
    },
    [getMutateAsync, onMutationError, onShowErrorSnackbar],
  );

  const setUserPreference = useCallback(
    async ({ applicationId, module, recordId, key, value }: SetUserPreferencePayload) => {
      try {
        const result = await setMutateAsync(
          {
            data: buildSetUserPreferencePayloadRequest({
              applicationId,
              module,
              recordId,
              key,
              value,
            }),
          },
          {
            onError: onMutationError,
          },
        );

        return (result.response as { userPreference: SetUserPreferencePayload } | undefined)
          ?.userPreference;
      } catch (error) {
        onShowErrorSnackbar();
      }
    },
    [setMutateAsync, onMutationError, onShowErrorSnackbar],
  );

  return {
    isPending: isSetPending || isGetPending,
    setUserPreference,
    getUserPreference,
    isLoading: isGetPending,
    error: setError ?? getError,
  };
}
